import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const apiUrl = process.env.REACT_APP_API_URL;

function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
  );
  return match ? match[1] : null;
}

function SortableVideoItem({ id, video, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const youTubeId = video.video_type === 'youtube' ? getYouTubeId(video.video_url) : null;

  return (
    <div ref={setNodeRef} style={style} className="col-sm-6 col-lg-4 col-xl-3">
      <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ position: 'relative' }}>
          {video.video_type === 'youtube' && youTubeId ? (
            <img
              src={`https://img.youtube.com/vi/${youTubeId}/hqdefault.jpg`}
              alt={video.title || 'YouTube video'}
              style={{ width: '100%', height: 160, objectFit: 'cover' }}
            />
          ) : (
            <div style={{
              width: '100%', height: 160, background: '#1f2937',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <i className="ph ph-play-circle text-white" style={{ fontSize: 42 }} />
            </div>
          )}
          <span
            className="badge position-absolute rounded-pill text-13 fw-medium text-white"
            style={{ top: 10, left: 10, padding: '4px 12px', backgroundColor: video.video_type === 'youtube' ? '#dc2626' : '#0d6efd' }}
          >
            {video.video_type === 'youtube' ? 'YouTube' : 'Uploaded'}
          </span>
          <span className="badge bg-main-600 text-white position-absolute rounded-pill text-13 fw-medium" style={{ top: 10, left: video.video_type === 'youtube' ? 88 : 98, padding: '4px 12px' }}>
            #{video.position}
          </span>
          <span
            {...attributes}
            {...listeners}
            className="position-absolute bg-white rounded-circle d-flex align-items-center justify-content-center shadow-sm"
            style={{ top: 10, right: 10, width: 32, height: 32, cursor: 'grab' }}
            title="Drag to reorder"
          >
            <i className="ph ph-dots-six-vertical text-gray-500" />
          </span>
        </div>
        <div className="card-body py-12 px-16">
          <span className="fw-semibold text-15 text-truncate d-block mb-10" title={video.title}>
            {video.title || 'Untitled'}
          </span>
          <button
            type="button"
            className="btn btn-sm rounded-pill py-6 px-16 w-100"
            style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff', opacity: 1 }}
            onClick={() => onDelete(video.id)}
          >
            <i className="ph ph-trash me-1" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminVideoGallery() {
  const [videos, setVideos] = useState([]);
  const [title, setTitle] = useState('');
  const [videoType, setVideoType] = useState('youtube');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchVideos();
  }, []);

  const getToken = () => {
    const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
    return sessionData?.token || null;
  };

  const fetchVideos = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) { setError('You must be logged in to manage the video gallery.'); setLoading(false); return; }
      const response = await axios.get(`${apiUrl}/api/admin/video-gallery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) setVideos(response.data.videos);
      else setError(response.data.message || 'Failed to fetch videos.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching videos.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setVideoUrl('');
    setVideoFile(null);
    setError('');
    setUploadProgress(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (videoType === 'youtube' && !videoUrl.trim()) { setError('Please enter a YouTube URL.'); return; }
    if (videoType === 'upload' && !videoFile) { setError('Please choose a video file to upload.'); return; }

    setSaving(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('video_type', videoType);
    if (videoType === 'youtube') {
      formData.append('video_url', videoUrl.trim());
    } else {
      formData.append('video', videoFile);
    }

    try {
      const token = getToken();
      if (!token) { setError('Authentication required.'); setSaving(false); return; }
      const response = await axios.post(`${apiUrl}/api/admin/video-gallery`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      });
      if (response.data.success) {
        setSuccess('Video added successfully.');
        resetForm();
        fetchVideos();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to add video.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error adding video.');
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video? This cannot be undone.')) return;
    try {
      const token = getToken();
      const response = await axios.delete(`${apiUrl}/api/admin/video-gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) fetchVideos();
      else setError(response.data.message || 'Failed to delete video.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting video.');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setVideos((items) => {
        const oldIndex = items.findIndex((item) => item.id.toString() === active.id);
        const newIndex = items.findIndex((item) => item.id.toString() === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
          ...item,
          position: index + 1,
        }));
        updatePositions(newOrder);
        return newOrder;
      });
    }
  };

  const updatePositions = async (updatedVideos) => {
    try {
      const token = getToken();
      await axios.put(`${apiUrl}/api/admin/video-gallery/order`, updatedVideos, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      setError('Failed to update video order.');
      fetchVideos();
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Video Gallery</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          <div className="card mb-24">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">Add Video</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-20">
                  <div className="col-sm-4">
                    <label htmlFor="video_type" className="h5 mb-8 fw-semibold font-heading">Type</label>
                    <select
                      id="video_type"
                      className="form-control py-11"
                      value={videoType}
                      onChange={(e) => { setVideoType(e.target.value); setVideoUrl(''); setVideoFile(null); }}
                      disabled={saving}
                    >
                      <option value="youtube">YouTube Link</option>
                      <option value="upload">Upload File</option>
                    </select>
                  </div>
                  <div className="col-sm-8">
                    <label htmlFor="title" className="h5 mb-8 fw-semibold font-heading">Title (optional)</label>
                    <input
                      type="text"
                      id="title"
                      className="form-control py-11"
                      placeholder="e.g. Campus Tour 2026"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      disabled={saving}
                    />
                  </div>

                  {videoType === 'youtube' ? (
                    <div className="col-12">
                      <label htmlFor="video_url" className="h5 mb-8 fw-semibold font-heading">
                        YouTube URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        id="video_url"
                        className="form-control py-11"
                        placeholder="https://www.youtube.com/watch?v=..."
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                  ) : (
                    <div className="col-12">
                      <label htmlFor="video_file" className="h5 mb-8 fw-semibold font-heading">
                        Video File <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        id="video_file"
                        className="form-control py-11"
                        accept="video/*"
                        onChange={(e) => setVideoFile(e.target.files[0] || null)}
                        disabled={saving}
                        style={{ maxWidth: 400 }}
                      />
                      <span className="text-13 text-gray-400 d-block mt-6">MP4, WebM, OGG or MOV. Max 100MB.</span>
                      {saving && videoType === 'upload' && uploadProgress > 0 && (
                        <div className="progress mt-10" style={{ height: 8, maxWidth: 400 }}>
                          <div
                            className="progress-bar bg-main-600"
                            role="progressbar"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="col-12 flex-align justify-content-end gap-8">
                    <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                      {saving ? (videoType === 'upload' ? `Uploading... ${uploadProgress}%` : 'Saving...') : 'Add Video'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="card">
            <div className="card-header border-bottom border-gray-100 flex-between">
              <h5 className="mb-0">All Videos ({videos.length})</h5>
              <span className="text-13 text-gray-400">
                <i className="ph ph-arrows-out-cardinal me-1" /> Drag cards to reorder
              </span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : videos.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No videos yet. Add one above.</div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={videos.map((v) => v.id.toString())} strategy={rectSortingStrategy}>
                    <div className="row g-16">
                      {videos.map((v) => (
                        <SortableVideoItem key={v.id} id={v.id.toString()} video={v} onDelete={handleDelete} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminVideoGallery;
